import React from "react";
import styles from "../CheckoutSection.module.scss";
import DynamicFontAwesomeIcon from "components/elements/DynamicFontAwesomeIcon";
import Input from "components/elements/Input/Input";
import Button from "components/elements/Button/Button";
import { formatCurrency } from "../helpers";

/**
 * Component for displaying order summary and price breakdown
 */
const OrderSummary = ({
	subtotal,
	discount,
	discountedSubtotal,
	total,
	tax,
	taxesByRate,
	currency,
	showPromoField,
	promoApplied,
	couponError,
	isApplyingPromo,
	formData,
	activeCoupon,
	onInputChange,
	onTogglePromoField,
	onApplyPromoCode,
	onRemovePromoCode
}) => {
	return (
		<div className={`${styles.orderSummary} mt-4`}>
			<hr className="my-4" />
			<div className="d-flex justify-content-between mb-2">
				<span>Zwischensumme</span>
				<span aria-label={`Zwischensumme: ${formatCurrency(subtotal, currency)}`}>
					{formatCurrency(subtotal, currency)}
				</span>
			</div>

            

			{/* Display discount if available */}
			{discount > 0 && (
				<div className="d-flex justify-content-between mb-2 text-success">
					<span>Rabatt {activeCoupon?.code && `(${activeCoupon.code})`}</span>
					<span aria-label={`Rabatt: ${formatCurrency(discount * -1, currency)}`}>
						-{formatCurrency(discount, currency)}
					</span>
				</div>
			)}

			{/* Display tax breakdowns by rate */}
			{Object.entries(taxesByRate || {}).map(([rate, amount]) => (
				<div className="d-flex justify-content-between mb-2 text-secondary small" key={`tax-${rate}`}>
					<span>inkl. MwSt. ({rate}%)</span>
					<span aria-label={`MwSt ${rate}%: ${formatCurrency(amount, currency)}`}>
						{formatCurrency(amount, currency)}
					</span>
				</div>
			))}

			<div className="d-flex justify-content-between fs-5 fw-bold mt-3">
				<span>Gesamtsumme</span>
				<span aria-label={`Gesamtsumme: ${formatCurrency(total, currency)}`}>
					{formatCurrency(total, currency)}
				</span>
			</div>

			{/* Promo code section */}
			<div className="mt-4">
				{!showPromoField && !promoApplied && (
					<Button
						variant="link"
						size="sm"
						className="p-0 text-white opacity-75"
						onClick={onTogglePromoField}
						title="Gutscheincode eingeben"
						aria-label="Gutscheincode eingeben">
						<DynamicFontAwesomeIcon 
							iconName="tag" 
							className="me-1" 
							aria-hidden="true" 
						/>
						Gutscheincode?
					</Button>
				)}

				{showPromoField && !promoApplied && (
					<div className={`mt-2 ${styles.promoField}`}>
						<div className="row g-2">
							<div className="col">
								<Input
									type="text"
									id="promoCode"
									name="promoCode"
									placeholder="Gutscheincode"
									value={formData.promoCode}
									onChange={onInputChange}
									size="sm"
									error={couponError}
									disabled={isApplyingPromo}
									aria-label="Gutscheincode eingeben"
									data-field="promoCode"
								/>
							</div>
							<div className="col-auto">
								<Button
									variant="light"
									size="sm"
									onClick={onApplyPromoCode}
									disabled={!formData.promoCode || isApplyingPromo}
									title="Gutscheincode anwenden"
									aria-label="Gutscheincode anwenden">
									{isApplyingPromo ? (
										<DynamicFontAwesomeIcon 
											iconName="spinner" 
											spin 
											aria-hidden="true" 
										/>
									) : "Anwenden"}
								</Button>
							</div>
						</div>
					</div>
				)}

				{promoApplied && activeCoupon && (
					<div className="d-flex align-items-center justify-content-between mt-2 bg-success bg-opacity-10 p-2 rounded">
						<span className="d-flex align-items-center">
							<DynamicFontAwesomeIcon 
								iconName="checkCircle" 
								className="text-success me-2" 
								aria-hidden="true" 
							/>
							<span>
								Gutschein <strong>{activeCoupon.code}</strong> angewendet
							</span>
						</span>
						<Button
							variant="link"
							size="sm"
							className="text-muted ms-2 p-0"
							onClick={onRemovePromoCode}
							title="Gutscheincode entfernen"
							aria-label="Gutscheincode entfernen">
							<DynamicFontAwesomeIcon 
								iconName="times" 
								aria-hidden="true" 
							/>
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default OrderSummary;
